// The "Registra richiesta di radiazione" dialog and the request step of the PRA case (Stage 2
// slice 6). Plain module with no Vue, so it runs under `node --test`. Labels are i18n keys; the API
// checks everything again (channel, agency, dates, file) and decides lateness.

import { todayRome } from "./draftForm.mjs";
import { checkUpload } from "./dossierFiles.mjs";

// Same values and limits as the API (constants/compliance.js PRA_CHANNELS, pra/manualConnector.js).
export const REQUEST_CHANNELS = ["rvfu_yard", "sta_agency", "other"];
export const AGENCY_NAME_MAX = 200;

export function emptyRequestForm() {
  return {
    date: "",
    channel: "",
    agencyName: "",
    file: null,
  };
}

// The request date goes from the CRD date to today in Europe/Rome (both included).
export function requestDateLimits({ crdDate, now = new Date() }) {
  return { min: crdDate || "", max: todayRome(now) };
}

// { field: reason } for what blocks the save; {} when it can be sent. "required" only disables the
// button; the other reasons show under the field. The agency is checked only for sta_agency; the
// reference and the file are optional.
export function requestFormErrors(form, limits) {
  const errors = {};
  const date = (form && form.date) || "";
  if (!date) errors.date = "required";
  else if (limits.min && date < limits.min) errors.date = "before_crd";
  else if (limits.max && date > limits.max) errors.date = "in_future";
  const channel = (form && form.channel) || "";
  if (!channel) errors.channel = "required";
  else if (!REQUEST_CHANNELS.includes(channel)) errors.channel = "invalid";
  if (channel === "sta_agency") {
    const agency = String(form.agencyName || "").trim();
    if (!agency) errors.agencyName = "required";
    else if (agency.length > AGENCY_NAME_MAX) errors.agencyName = "too_long";
  }
  if (form && form.file) {
    const problem = checkUpload(form.file, "pra");
    if (problem) errors.file = problem;
  }
  return errors;
}

// The live amber warning: a request dated after the due date is recorded as late (never blocked).
// The due date itself is on time, as in the API (pra/rules.js isRequestLate).
export function isRequestLate(date, dueDate) {
  return Boolean(date && dueDate && date > dueDate);
}

// What the dialog sends: the agency only for sta_agency, empty optional values left out.
export function requestPayload(form) {
  const payload = { date: form.date, channel: form.channel };
  if (form.channel === "sta_agency") {
    payload.agencyName = String(form.agencyName || "").trim();
  }
  if (form.file) payload.file = form.file;
  return payload;
}

// One commandId per request: a retry of the same input reuses it, so a double click records it once.
export function requestSubmitKey(payload) {
  const file = payload.file || {};
  return [
    payload.date,
    payload.channel,
    payload.agencyName || "",
    file.name || "",
    file.size || "",
    file.lastModified || "",
  ].join("|");
}

// The "Registra richiesta di radiazione" button: shown while the case waits for the request,
// disabled on hold.
export function requestAction(view) {
  const praCase = view && view.praCase;
  if (!praCase || view.nextStep !== "request") return null;
  return { disabled: Boolean(praCase.activeHold) };
}

// A failed request save -> acceptance.pra.request.errors.<key>, or null to show the API's errorMsg.
const REQUEST_ERROR_CODES = [
  "date_order",
  "date_in_future",
  "unsupported_type",
  "step_out_of_order",
  "already_confirmed",
  "case_on_hold",
];

export function requestErrorKey(err) {
  const res = err && err.response;
  if (!res) return null;
  // A proxy in front of the API may answer 413 without our JSON body.
  if (res.status === 413) return "too_large";
  const data = res.data || {};
  if (
    data.code === "invalid_input" &&
    data.details &&
    data.details.field === "request.agencyName"
  ) {
    return "agency_required";
  }
  return REQUEST_ERROR_CODES.includes(data.code) ? data.code : null;
}
